import { DeleteOutlined } from '@ant-design/icons'
import { Button } from '@sureco/design-system'
import { Modal } from '@surecompanies/core_components'
import { message } from 'antd'
import {
  ActivityLogAction,
  postActivityLog,
} from 'modules/activity-log-service'
import React, { useState } from 'react'
import { updateCensusFileRows } from '../../api/census-enrollment-platform-api-client'
import { CensusFileRowDto } from '../../api/dto/census-file-row.dto'
import { CensusFileRowsSelectAllDto } from '../../api/dto/census-file-rows-select-all.dto'
import { CensusFileRowStatusEnum } from '../../api/enums/census-file-row-status.enum'
import { CensusFileRowTypeEnum } from '../../api/enums/census-file-row-type.enum'
import { PatchActionEnum } from '../../api/enums/patch-action.enum'
import { CensusFileRowGridData } from '../../types/census-file-row-grid-data'
import { mapGridDataToCensusFileRowDto } from '../../utils/map-grid-data-to-census-file-row-dto'

interface RejectSelectedButtonProps {
  selectedRows: CensusFileRowGridData[]
  handleSubmit: (rows: CensusFileRowGridData[]) => void
  cleanAllRows?: () => void
  setLoading: (loading: boolean) => void // Prop to update loading state in parent
  buttonText?: string
  type?: CensusFileRowTypeEnum
  publicKey: string
  selectAllPayload?: CensusFileRowsSelectAllDto
  totalCount?: number
}

// Set state of rows to Rejected
const rejectRows = async (
  rowsToReject: CensusFileRowGridData[],
  publicKey: string
) => {
  const rejectedRows: CensusFileRowDto[] = rowsToReject.map((gridRow) => ({
    ...mapGridDataToCensusFileRowDto(gridRow),
    status: CensusFileRowStatusEnum.REJECTED,
  }))
  await updateCensusFileRows(
    rejectedRows,
    publicKey,
    PatchActionEnum.REJECT_RECORDS
  )
}

const RejectSelectedButton = ({
  selectedRows,
  handleSubmit,
  cleanAllRows,
  setLoading,
  buttonText,
  type,
  publicKey,
  selectAllPayload,
  totalCount,
}: RejectSelectedButtonProps) => {
  const [loading, setButtonLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const displayCount = selectAllPayload
    ? (totalCount ?? selectedRows.length)
    : selectedRows.length

  const handleClick = () => {
    if (selectAllPayload) {
      setShowConfirmModal(true)
    } else {
      confirmReject()
    }
  }

  const confirmReject = async () => {
    setShowConfirmModal(false)

    let timer: NodeJS.Timeout | null = null
    let shouldShowLoading = false

    // Start a delayed loading indicator (only if the request takes longer than 1000ms)
    timer = setTimeout(() => {
      setButtonLoading(true)
      setLoading(true)
      shouldShowLoading = true
    }, 800)

    try {
      // Use selectAll payload if available, otherwise send individual rows
      if (selectAllPayload) {
        await updateCensusFileRows(
          selectAllPayload,
          publicKey,
          PatchActionEnum.REJECT_RECORDS
        )
      } else {
        await rejectRows(selectedRows, publicKey)
      }
      message.success(`${displayCount} records rejected`, 3)
      postActivityLog({
        action: ActivityLogAction.CENSUS_REVIEW_REJECT,
        description: `rejected ${displayCount} census review records`,
        additionalData: {
          publicKey,
          recordCount: displayCount,
          selectAll: !!selectAllPayload,
          rows: selectedRows,
        },
      })
    } catch (error) {
      const msg = `Failed to reject records: '${(error as Error).message}'`
      console.error(msg)
      message.error(msg, 5)
    } finally {
      // Clear loading state if it was set
      if (shouldShowLoading) {
        setButtonLoading(false)
        setLoading(false)
      }
      if (timer) {
        clearTimeout(timer)
      }
      handleSubmit(selectedRows)
      cleanAllRows?.()
    }
  }

  if (selectedRows.length === 0 && !selectAllPayload) return null

  const duplicateButton = (
    <Button onClick={handleClick} size="large" loading={loading}>
      Reject All
    </Button>
  )

  return (
    <>
      {type === CensusFileRowTypeEnum.DUPLICATE_EMPLOYEE ? (
        duplicateButton
      ) : (
        <Button
          onClick={handleClick}
          variant="danger"
          size="large"
          loading={loading}
        >
          <DeleteOutlined style={{ marginRight: '8px' }} />
          {loading ? 'Rejecting...' : buttonText || `Reject (${displayCount})`}
        </Button>
      )}

      <Modal
        open={showConfirmModal}
        title="Confirm Reject All"
        onCancel={() => setShowConfirmModal(false)}
        onOk={confirmReject}
        okText="Yes, Reject All"
        cancelText="No, Cancel"
        okButtonProps={{ danger: true }}
      >
        <p style={{ textAlign: 'center' }}>Are you sure?</p>
        <p style={{ textAlign: 'center' }}>
          This will reject all <strong>{displayCount}</strong> records. This
          action cannot be undone.
        </p>
      </Modal>
    </>
  )
}

export default RejectSelectedButton
